import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ClipboardPaste, ShoppingCart, AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { PageHeading } from "@/components/states";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { buildQuickOrder } from "@/lib/quickOrder";
import {
  formatCurrency,
  getLocalizedField,
  isDeliverableInCountry,
  isOrderable,
  normalizeQuantity,
} from "@/lib/product";

const Snelbestellen = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { producten, account } = useAuth();
  const { addLine } = useCart();
  const navigate = useNavigate();

  const [catalogus, setCatalogus] = useState([]);
  const [tekst, setTekst] = useState("");
  const [regels, setRegels] = useState(null);
  const [aantallen, setAantallen] = useState({});

  useEffect(() => {
    producten()
      .then((data) => setCatalogus(Array.isArray(data) ? data.filter((p) => isDeliverableInCountry(p, account)) : []))
      .catch(() => setCatalogus([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const controleren = useCallback(() => {
    const lijst = buildQuickOrder(tekst, catalogus);
    setRegels(lijst);
    setAantallen(
      lijst.reduce((acc, r) => {
        if (r.product) acc[r.key] = normalizeQuantity(r.product, r.aantal);
        return acc;
      }, {}),
    );
  }, [tekst, catalogus]);

  const { gevonden, voorstellen, nietGevonden, totaal } = useMemo(() => {
    const lijst = regels || [];
    const bestelbaar = lijst.filter((r) => r.product && isOrderable(r.product, account));
    return {
      gevonden: bestelbaar.filter((r) => r.zeker),
      voorstellen: bestelbaar.filter((r) => !r.zeker),
      nietGevonden: lijst.filter((r) => !r.product || !isOrderable(r.product, account)),
      totaal: bestelbaar.reduce(
        (sum, r) => sum + Number(r.product.verkoopprijs || 0) * Number(aantallen[r.key] || 0),
        0,
      ),
    };
  }, [regels, aantallen, account]);

  const toevoegen = () => {
    const teVoegen = [...gevonden, ...voorstellen];
    if (teVoegen.length === 0) return;
    teVoegen.forEach((r) => addLine(r.product, Number(aantallen[r.key]) || 1));
    toast.success(t("quickOrder.added", { count: teVoegen.length }));
    navigate("/mandje");
  };

  const Regel = ({ regel, voorstel }) => {
    const naam = getLocalizedField(regel.product, "naam", lang);
    return (
      <div
        className="flex flex-wrap items-center gap-3 py-3"
        data-testid="quick-order-row"
        data-sku={regel.product?.sku || ""}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug">{naam}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {regel.product?.sku} · {formatCurrency(regel.product?.verkoopprijs, lang)} {t("price.exclVat")}
          </p>
          {voorstel && (
            <p className="mt-1 text-xs font-medium text-[hsl(var(--warning))]" data-testid="quick-order-suggestion-note">
              {t("quickOrder.suggestion", { invoer: regel.invoer })}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor={`qo-${regel.key}`} className="text-xs text-muted-foreground">
            {t("cart.quantity")}
          </Label>
          <Input
            id={`qo-${regel.key}`}
            type="number"
            min="1"
            value={aantallen[regel.key] ?? regel.aantal}
            onChange={(e) => setAantallen((prev) => ({ ...prev, [regel.key]: e.target.value }))}
            onBlur={() =>
              setAantallen((prev) => ({ ...prev, [regel.key]: normalizeQuantity(regel.product, prev[regel.key]) }))
            }
            className="mt-1 w-24"
            data-testid="quick-order-quantity-input"
          />
        </div>
        <div className="min-w-[92px] text-right text-sm font-semibold tabular-nums" data-testid="quick-order-row-total">
          {formatCurrency(Number(regel.product?.verkoopprijs || 0) * Number(aantallen[regel.key] || 0), lang)}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="quick-order-page">
      <PageHeading title={t("quickOrder.title")} subtitle={t("quickOrder.subtitle")} testId="quick-order-heading" />

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <Card className="p-5 lg:col-span-6">
          <Label htmlFor="quick-order-input" className="text-sm font-medium">
            {t("quickOrder.inputLabel")}
          </Label>
          <Textarea
            id="quick-order-input"
            rows={12}
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
            placeholder={"NB1064;12\nFR1005 6\nFR1001, 24"}
            className="mt-2 font-mono text-sm"
            data-testid="quick-order-input"
          />
          <p className="mt-2 text-xs text-muted-foreground">{t("quickOrder.inputHint")}</p>
          <Button className="mt-4 w-full" onClick={controleren} disabled={!tekst.trim()} data-testid="quick-order-check-button">
            <ClipboardPaste className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("quickOrder.check")}
          </Button>
        </Card>

        <div className="space-y-4 lg:col-span-6">
          {!regels && (
            <Card className="p-5 text-sm text-muted-foreground" data-testid="quick-order-empty">
              {t("quickOrder.empty")}
            </Card>
          )}

          {regels && (
            <>
              {gevonden.length > 0 && (
                <Card className="p-5" data-testid="quick-order-found">
                  <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
                    <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                    {t("quickOrder.found", { count: gevonden.length })}
                  </h2>
                  <div className="mt-2 divide-y">
                    {gevonden.map((r) => <Regel key={r.key} regel={r} />)}
                  </div>
                </Card>
              )}

              {voorstellen.length > 0 && (
                <Card className="p-5" data-testid="quick-order-suggestions">
                  <h2 className="font-display text-lg font-semibold">
                    {t("quickOrder.suggestions", { count: voorstellen.length })}
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">{t("quickOrder.suggestionsHint")}</p>
                  <div className="mt-2 divide-y">
                    {voorstellen.map((r) => <Regel key={r.key} regel={r} voorstel />)}
                  </div>
                </Card>
              )}

              {nietGevonden.length > 0 && (
                <Alert
                  className="border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"
                  data-testid="quick-order-unmatched"
                >
                  <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
                  <AlertDescription>
                    {t("quickOrder.unmatched", { count: nietGevonden.length })}
                    <ul className="mt-2 space-y-1 font-mono text-xs">
                      {nietGevonden.map((r) => <li key={r.key}>{r.invoer}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <Card className="p-5">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium">{t("cart.totalExcl")}</span>
                  <span className="text-lg font-semibold tabular-nums" data-testid="quick-order-total">
                    {formatCurrency(totaal, lang)}
                  </span>
                </div>
                <Separator className="my-4" />
                <Button
                  className="w-full"
                  onClick={toevoegen}
                  disabled={gevonden.length + voorstellen.length === 0}
                  data-testid="quick-order-add-all-button"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("quickOrder.addAll", { count: gevonden.length + voorstellen.length })}
                </Button>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Snelbestellen;
